// lib/print.ts — Direct Print: send a PDF, an image or an HTML page straight to
// the browser's print dialog without opening it in a new tab.
//
// How: the content is loaded into a hidden, same-origin iframe (a blob: URL or
// srcdoc) and that frame's own print() is called — the same technique the ED
// referral letter and the pharmacy transfer slip already use, now shared.
// The Content Security Policy blocks inline scripts, so nothing here relies on
// a <script> inside the printed page.
//
// Guarantees:
//   • One job per key at a time — a double click does not print twice
//     (isPrinting/printJob). Printing is read-only; it never creates a bill.
//   • If the browser refuses to print from a frame (some embedded browsers and
//     PDF viewers do), the document opens in a new tab instead so the user can
//     print from there, and the caller is told (`fallback: true`).
//   • The frame and its blob URL are cleaned up after the dialog closes.

export interface PrintResult {
  /** True when the document was opened in a new tab instead of printed directly. */
  fallback: boolean;
}

const inFlight = new Set<string>();

/** Whether a print job with this key is still being prepared. */
export function isPrinting(key: string): boolean {
  return inFlight.has(key);
}

/** Runs `job` unless a job with the same key is already running (then resolves null). */
export async function printJob(key: string, job: () => Promise<PrintResult>): Promise<PrintResult | null> {
  if (inFlight.has(key)) return null;
  inFlight.add(key);
  try { return await job(); }
  finally { inFlight.delete(key); }
}

function hiddenFrame(): HTMLIFrameElement {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  // Off-screen but laid out at a real page size: some engines skip images and
  // PDF rendering in a 0×0 frame.
  frame.style.position = 'fixed';
  frame.style.left = '-10000px';
  frame.style.top = '0';
  frame.style.width = '800px';
  frame.style.height = '1100px';
  frame.style.border = '0';
  frame.style.opacity = '0';
  frame.style.pointerEvents = 'none';
  return frame;
}

function removeLater(frame: HTMLIFrameElement, url?: string) {
  // Long enough for the print dialog's own rendering to finish; the browser
  // keeps what it needs once printing has started.
  setTimeout(() => {
    frame.remove();
    if (url) URL.revokeObjectURL(url);
  }, 60_000);
}

function openFallback(url: string) {
  const w = window.open(url, '_blank', 'noopener');
  if (!w) throw new Error('The browser blocked printing and pop-ups. Use Download, then print the file.');
}

/** Loads `setup` into a hidden frame and prints it. */
function printInFrame(setup: (frame: HTMLIFrameElement) => void, fallbackUrl?: string): Promise<PrintResult> {
  return new Promise((resolve, reject) => {
    const frame = hiddenFrame();
    let settled = false;
    const done = (r: PrintResult | Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (r instanceof Error) reject(r); else resolve(r);
    };
    const tryFallback = () => {
      if (!fallbackUrl) { done(new Error('This browser could not print the document. Use Download, then print the file.')); return; }
      try { openFallback(fallbackUrl); done({ fallback: true }); } catch (e) { done(e as Error); }
    };
    frame.onload = () => {
      // A PDF viewer inside the frame needs a moment after load before it can print.
      setTimeout(() => {
        try {
          const win = frame.contentWindow;
          if (!win) throw new Error('no frame window');
          win.focus();
          win.print();
          done({ fallback: false });
        } catch {
          tryFallback();
        } finally {
          removeLater(frame, fallbackUrl);
        }
      }, 250);
    };
    // If the frame never loads (blocked content), don't leave the caller waiting.
    const timer = setTimeout(() => { tryFallback(); removeLater(frame, fallbackUrl); }, 15_000);
    setup(frame);
    document.body.appendChild(frame);
  });
}

/** Prints a fetched file: a PDF directly, an image wrapped in a page sized to fit. */
export function printBlob(blob: Blob, opts: { title?: string } = {}): Promise<PrintResult> {
  const type = (blob.type || '').toLowerCase();
  if (type.startsWith('image/')) {
    const url = URL.createObjectURL(blob);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(opts.title || 'Document')}</title>
      <style>@page{margin:10mm}html,body{margin:0}img{display:block;max-width:100%;max-height:100vh;margin:0 auto;object-fit:contain}</style>
      </head><body><img src="${url}" alt=""></body></html>`;
    return printInFrame((f) => { f.srcdoc = html; }, url);
  }
  if (type && !type.includes('pdf') && !type.startsWith('text/html')) {
    return Promise.reject(new Error('This file type cannot be printed from the browser. Use Download and print it from its own app.'));
  }
  const url = URL.createObjectURL(blob);
  return printInFrame((f) => { f.src = url; }, url);
}

/** Prints a self-contained HTML page (inline styles, no scripts). */
export function printHtml(html: string): Promise<PrintResult> {
  const fallbackUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  return printInFrame((f) => { f.srcdoc = html; }, fallbackUrl);
}

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
